import { useApolloClient } from '@apollo/client';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { CreditCardSetupInitiatorDialog } from './CreditCardSetupInitiatorDialog';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import type { NormalizableGraphError } from '@shared/utils/graphError';
import type { ShippingAddressFragment } from '@graphql/fragments/shippingAddress.graphql.generated';
import { EventUserStatus, RegistryPayoutAccountWithdrawalStatus } from '@graphql/generated-base';
import type { RegistryPayoutAccountExternalAccountPayoutMethod } from '@graphql/generated-base';
import type { ExternalAccount } from '@shared/components/PayoutDialog/PayoutDialog.types';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary/ChunkErrorBoundary';
import { useCreateRegistryPayoutAccountMutation } from '@graphql/mutations/registry.graphql.generated';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { GetPayoutAccountInfoDocument, useGetPayoutAccountInfoLazyQuery } from '@graphql/queries/registry.graphql.generated';
import type { GetPayoutAccountInfoQuery, GetPayoutAccountInfoQueryVariables } from '@graphql/queries/registry.graphql.generated';
import { useGetPayoutAccountOnboardingUrlLazyQuery, useInitCashFundsHostFromRegistryMutation } from './CreditCardSetup.graphql.generated';
import { useAuth } from '@shared/components/AuthProvider';
import { normalizeGraphError, useTranslateGraphError, errorFromGraphError } from '@shared/utils/graphError';
import { useTranslation } from '@shared/core';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import type { PayoutAccountInfo } from './CreditCardSetUp.context';
import { Provider } from './CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { PayoutDialog } from './PayoutDialog/PayoutDialog';
import { usePayoutDialogController } from './PayoutDialog/PayoutDialog.controller';
import { useRegistryPayoutAccountSession } from '@shared/components/PayoutBankAccountDialog/useRegistryPayoutAccountSession';
import { LazyPartnerTransferSheet } from '@shared/components/TransferSheet/adapter/PartnerTransferSheet.lazy';
import { useTransferSheetRollout } from '@shared/components/TransferSheet/adapter/useTransferSheetRollout';
import { isAddressValid, isUSAddress } from '@apps/registry/common/utils/shippingAddressValidations';
import { usePreviousValue } from '@shared/utils/hooks/usePreviousValue';
import { hasActiveStripeAccount } from '@shared/utils/payoutAccount';
import { canWithdrawFunds, isCashEquivalentSupported } from './CreditCardSetup.utils';
import { usePostStripeReturnEffect } from './useStripeReturnEffects';
import { useCreditCardSetupTelemetry } from './CreditCardSetUp.telemetry';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';
import { addError } from '@shared/utils/logger';
import { useResolvedCashFundLocale } from '@shared/utils/currency/useResolvedCashFundLocale';

const CHECK_STRIPE_STATUS_PARAM = 'check_stripe_status';
export const PAYOUT_REFRESH_PARAM = 'payoutRefresh';

const mapRegistryExternalAccount = (
  account: { __typename?: string; bankName?: string | null; brand?: string; last4: string; availablePayoutMethods: ReadonlyArray<string> } | null | undefined,
): ExternalAccount | null => {
  if (!account) return null;
  return {
    institutionName: account.__typename === 'RegistryPayoutAccountBankExternalAccount' ? (account.bankName ?? null) : (account.brand ?? null),
    last4: account.last4,
    availablePayoutMethods: account.availablePayoutMethods as ReadonlyArray<'instant' | 'standard'>,
  };
};

// Strips the leading `/{eventHandle}` segment off a pathname. `returnRoute` must be handle-relative:
//   the backend always prepends its own event base URL (origin + handle) onto whatever we send here,
//   with no check for whether it's already absolute -- sending a fully-qualified URL (or a bare path
//   that still carries the handle, since `window.location.pathname` always does) gets that base URL
//   prefixed on top anyway, duplicating it in the final redirect URL.
const stripEventHandleSegment = (pathname: string): string => {
  const segments = pathname.split('/');
  segments.splice(1, 1);
  return segments.join('/') || '/';
};

const appendStripeStatusMarker = (route: string): string => {
  const url = new URL(route, 'https://placeholder.invalid');
  url.searchParams.set(CHECK_STRIPE_STATUS_PARAM, 'true');
  return `${url.pathname}${url.search}${url.hash}`;
};

// Deliberately NOT `check_stripe_status`: that marker drives `decidePostStripeReturnAction`,
//   which opens the KYC setup dialog on success. A couple who only topped up their verification
//   should land back on the widget they left, with a forced status re-read and nothing else.
const appendPayoutRefreshMarker = (route: string): string => {
  const url = new URL(route, 'https://placeholder.invalid');
  url.searchParams.set(PAYOUT_REFRESH_PARAM, 'true');
  return `${url.pathname}${url.search}${url.hash}`;
};

// we're playing with this until we figure out the right amount of attempts and time.
const MAX_ATTEMPTS = 4;

export type CreditCardSetupProviderProps = Readonly<{
  children: React.ReactNode;
  eventId: string;
  registryShippingAddress: Readonly<ShippingAddressFragment> | null;
  handleInvalidShippingAddress?: (firstName?: string, lastName?: string) => void;
  handleChangeCurrency?: () => void;
  registryCurrencyCode?: string;
}>;

const CreditCardSetupProvider: React.FC<CreditCardSetupProviderProps> = ({
  children,
  eventId,
  registryShippingAddress,
  handleInvalidShippingAddress,
  handleChangeCurrency,
  registryCurrencyCode,
}) => {
  const openWindow = useWindowFunction('open');
  const {
    currentUser: { activeEventUser },
    isSuperAdmin,
  } = useAuth();
  const { toast } = useToast();
  const { translateGraphErrorCodeOrUnknown } = useTranslateGraphError();
  const { tq } = useTranslation('sharedRegistry');
  const telemetry = useCreditCardSetupTelemetry();
  const registryTelemetry = useAdminRegistryTelemetry();
  const [payoutAccountInfo, setPayoutAccountInfo] = useState<PayoutAccountInfo | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [hasKYCFailed, setHasKYCFailed] = useState(false);
  const maxAttemptsReached = attempts >= MAX_ATTEMPTS;
  const canManagePayouts = activeEventUser?.status === EventUserStatus.owner || isSuperAdmin;
  const isValidUSAddress = useMemo(() => isAddressValid(registryShippingAddress) && isUSAddress(registryShippingAddress), [registryShippingAddress]);
  const shouldSkipAddressValidation = !!payoutAccountInfo && hasActiveStripeAccount(payoutAccountInfo.status);
  const {
    countryCode: resolvedCountryCode,
    currencyCode: resolvedCurrencyCode,
    loading: resolvedLocaleLoading,
    supported: resolvedLocaleSupported,
  } = useResolvedCashFundLocale(registryCurrencyCode);
  const handleMutationError = useCallback(
    (result: NormalizableGraphError, label: string) => {
      const normalized = normalizeGraphError(result);
      // Registry refuses an account in another currency than the registry's. Get Started asks for the
      //   registry currency when cash funds support it, else USD; so with support, a refusal means the
      //   registry currency changed after this page loaded.
      const currencyMismatchMessage = resolvedLocaleSupported
        ? tq`Your registry currency changed since this page loaded. Reload the page and try again.`
        : tq`Card gifts for your registry can only be paid out in US Dollars right now. Change your registry currency to US Dollars to continue.`;
      const errorMessage = normalized?.code === 'PAYOUT_ACCOUNT_CURRENCY_MISMATCH' ? currencyMismatchMessage : translateGraphErrorCodeOrUnknown(normalized?.code);
      toast(errorMessage, { icon: <WarningTriangle />, duration: 10000 });
      telemetry.trackError(label, errorFromGraphError(result), {
        eventId,
        code: normalized?.code,
        nativeMessage: normalized?.nativeMessage,
      });
    },
    [eventId, toast, translateGraphErrorCodeOrUnknown, tq, resolvedLocaleSupported, telemetry],
  );

  // `useLazyQuery`'s execute function is void-returning in this Apollo Client version (3.4.x) --
  //   it kicks off the request but gives callers no way to await its settlement. `fetchPayoutInfo`
  //   below needs a real promise (so `usePayoutMethodUpdatedToast`'s `settleAfterRefresh` can wait
  //   on *a* completion of this query), so every in-flight caller's resolver is collected here and
  //   flushed together whenever the query next settles, success or error.
  const pendingFetchResolversRef = useRef<Set<() => void>>(new Set());
  const settlePendingFetches = useCallback(() => {
    pendingFetchResolversRef.current.forEach(resolve => resolve());
    pendingFetchResolversRef.current.clear();
  }, []);

  const payoutClient = useApolloClient();
  const applyPayoutInfo = useCallback(
    (data: GetPayoutAccountInfoQuery) => {
      // The reads here are no-cache, so copy the result into the plain {eventId} entry that the
      // currency lock and other readers use; otherwise they keep a "no account" from earlier in the session.
      payoutClient.writeQuery<GetPayoutAccountInfoQuery, GetPayoutAccountInfoQueryVariables>({ query: GetPayoutAccountInfoDocument, variables: { eventId }, data });
      const pa = data.getRegistryEventData?.payoutAccount;
      if (!pa) {
        setPayoutAccountInfo(null);
        return;
      }
      setPayoutAccountInfo({
        status: pa.status as PayoutAccountInfo['status'],
        statusDetails: pa.statusDetails ?? '',
        currencyCode: pa.currencyCode,
        countryCode: pa.countryCode,
        onboardingUrl: pa.onboardingUrl ?? '',
        serviceDashboardUrl: pa.serviceDashboardUrl ?? '',
        externalAccountLast4: pa.externalAccountLast4 ?? null,
        balance: pa.balance ?? undefined,
        withdrawals: pa.withdrawals ?? [],
        defaultExternalAccount: mapRegistryExternalAccount(pa.defaultExternalAccount),
        standardPayoutFee: pa.standardPayoutFee ?? null,
        instantPayoutFee: pa.instantPayoutFee ?? null,
        payoutThrottleDelayMs: pa.payoutThrottleDelayMs ?? null,
        nextWithdrawalAvailableAt: pa.nextWithdrawalAvailableAt ?? null,
        payoutLimitInMinorUnits: pa.payoutLimitInMinorUnits ?? null,
      });
    },
    [payoutClient, eventId],
  );

  const [getPayoutInfo, { loading: fetchingPayoutInfo, called: hasFetchedPayoutInfo }] = useGetPayoutAccountInfoLazyQuery({
    batchMode: 'fast',
    // `no-cache` is why passing an argument to `payoutAccount(options:)` is safe here: the forced
    //   read never lands in the cache under its own key. `applyPayoutInfo` copies each result into
    //   the plain {eventId} entry on purpose, so that entry always holds the latest read. Restoring a
    //   caching fetchPolicy would let forced and unforced reads share a key. Don't "fix" this to cache-first.
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      applyPayoutInfo(data);
      settlePendingFetches();
    },
    onError: () => {
      settlePendingFetches();
    },
  });
  const [createPayoutAccount, { loading: initPayoutAccountLoading }] = useCreateRegistryPayoutAccountMutation({
    onCompleted: data => {
      const createRegistryPayoutAccount = data.createRegistryPayoutAccount;
      if (createRegistryPayoutAccount.__typename === 'RegistryPayoutAccount') {
        const url = createRegistryPayoutAccount.onboardingUrl ?? '';
        if (url) openWindow(url, '_self');
      } else if (createRegistryPayoutAccount.__typename === 'RegistryErrorResult') {
        setHasKYCFailed(true);
        handleMutationError(createRegistryPayoutAccount, 'CreateRegistryPayoutAccount');
      }
    },
    onError: error => {
      setHasKYCFailed(true);
      addError(error, { context: 'CreditCardSetup.createPayoutAccount', eventId });
      telemetry.trackError('CreateRegistryPayoutAccount', error, { eventId });
      toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
    },
  });

  const [getPayoutAccountOnboardingUrl, { loading: fetchingOnboardingUrl }] = useGetPayoutAccountOnboardingUrlLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const account = data.getRegistryEventData?.payoutAccount;
      const url = account?.onboardingUrl || account?.serviceDashboardUrl;
      if (url) {
        openWindow(url, '_self');
      } else {
        setHasKYCFailed(true);
        addError(new Error('No onboarding URL for existing payout account'), { context: 'CreditCardSetup.getPayoutAccountOnboardingUrl', eventId });
        toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
      }
    },
    onError: error => {
      setHasKYCFailed(true);
      addError(error, { context: 'CreditCardSetup.getPayoutAccountOnboardingUrl', eventId });
      telemetry.trackError('GetPayoutAccountOnboardingUrl', error, { eventId });
      toast(translateGraphErrorCodeOrUnknown(undefined), { icon: <WarningTriangle />, duration: 10000 });
    },
  });

  const [initCashFundsHost, { loading: initKYCLaterLoading }] = useInitCashFundsHostFromRegistryMutation({
    onCompleted: data => {
      const result = data.initCashFundsHost;
      if (result.__typename === 'CashFundsHost') {
        getPayoutInfo({ variables: { eventId } });
      } else if (result.__typename === 'CashFundGeneralError') {
        handleMutationError(result, 'InitCashFundsHostFromRegistry');
      }
    },
    onError: error => {
      addError(error, { context: 'CreditCardSetup.initCashFundsHost', eventId });
      telemetry.trackError('InitCashFundsHostFromRegistry', error, { eventId });
    },
  });

  const [syncRegistryCurrency] = useSetRegistryCurrencyMutation();
  const initPayoutAccountOrLocaleLoading = initPayoutAccountLoading || resolvedLocaleLoading || fetchingOnboardingUrl;
  const isLocaleSupported = resolvedLocaleSupported !== null;
  const isGetCashEquivalentSupported = useMemo(() => isCashEquivalentSupported({ payoutAccountInfo, registryCurrencyCode }), [payoutAccountInfo, registryCurrencyCode]);
  const initKYCOnboarding = useCallback(() => {
    // Handle-relative and not a fully-qualified URL: see `stripEventHandleSegment` above.
    const returnRoute = withWindow(w => appendStripeStatusMarker(stripEventHandleSegment(w.location.pathname)), '');
    if (payoutAccountInfo) {
      setHasKYCFailed(false);
      getPayoutAccountOnboardingUrl({ variables: { eventId, returnRoute } });
      return;
    }
    if (resolvedLocaleLoading) return;
    setHasKYCFailed(false);
    void createPayoutAccount({ variables: { eventId, returnRoute, payload: { currencyCode: resolvedCurrencyCode, countryCode: resolvedCountryCode } } });
  }, [eventId, createPayoutAccount, getPayoutAccountOnboardingUrl, payoutAccountInfo, resolvedCountryCode, resolvedCurrencyCode, resolvedLocaleLoading]);
  const initKYCLaterOnboarding = useCallback(
    async (firstName: string, lastName: string, currencyCode: string, countryCode: string): Promise<boolean> => {
      try {
        const result = await initCashFundsHost({
          variables: {
            input: {
              eventId,
              firstName,
              lastName,
              currencyCode,
              address: { countryCode },
            },
          },
        });
        const success = result.data?.initCashFundsHost.__typename === 'CashFundsHost';
        if (success && registryCurrencyCode && currencyCode !== registryCurrencyCode) {
          await syncRegistryCurrency({
            variables: { eventId, payload: { code: currencyCode } },
            refetchQueries: () => ['GetAdminRegistryData', 'PublicShopRoot'],
          });
        }
        return success;
      } catch (error) {
        addError(error, { context: 'CreditCardSetup.initKYCLaterOnboarding', eventId });
        return false;
      }
    },
    [eventId, initCashFundsHost, registryCurrencyCode, syncRegistryCurrency],
  );
  const initKYCOnboardingFromRefund = useCallback(
    (returnRoute: string) => {
      const routeWithMarker = appendStripeStatusMarker(returnRoute);
      if (payoutAccountInfo) {
        setHasKYCFailed(false);
        getPayoutAccountOnboardingUrl({ variables: { eventId, returnRoute: routeWithMarker } });
        return;
      }
      if (resolvedLocaleLoading) return;
      setHasKYCFailed(false);
      void createPayoutAccount({ variables: { eventId, returnRoute: routeWithMarker, payload: { currencyCode: resolvedCurrencyCode, countryCode: resolvedCountryCode } } });
    },
    [eventId, createPayoutAccount, getPayoutAccountOnboardingUrl, payoutAccountInfo, resolvedCountryCode, resolvedCurrencyCode, resolvedLocaleLoading],
  );
  /**
   * Send a couple whose account Stripe has blocked back into Stripe's hosted flow to supply
   * whatever is outstanding. Unlike `initKYCOnboarding` this never creates an account -- it is
   * only reachable when one already exists -- and it returns to the widget the couple left
   * rather than the setup dialog.
   *
   * The link is minted HERE, on click, and never read off the page-load fragment: Stripe Account
   * Links are single-use and expire in ~5 minutes, so a link fetched at render time is usually
   * dead by the time anyone clicks it.
   */
  const initKYCRemediation = useCallback(
    (returnRoute?: string) => {
      if (!payoutAccountInfo) return;
      // An explicit `returnRoute` (e.g. from a caller building its own path) is expected to already
      //   be handle-relative, matching the convention below -- only the derived default needs stripping.
      const route = returnRoute ?? withWindow(w => `${stripEventHandleSegment(w.location.pathname)}${w.location.search}`, '');
      setHasKYCFailed(false);
      getPayoutAccountOnboardingUrl({ variables: { eventId, returnRoute: appendPayoutRefreshMarker(route) } });
    },
    [eventId, getPayoutAccountOnboardingUrl, payoutAccountInfo],
  );

  // `forceRefresh` makes the backend skip its cached copy and read the payout account live from
  //   Stripe. That is a real round trip across two services, so it stays opt-in per call: only the
  //   bank-account dialog's close handler passes it. The pending-retry loop and the mount fetch
  //   below deliberately call this unforced.
  const fetchPayoutInfo = useCallback(
    (opts?: { forceRefresh?: boolean }) => {
      getPayoutInfo({ variables: opts?.forceRefresh ? { eventId, options: { forceRefresh: true } } : { eventId } });
      // The execute function above is void-returning (see the comment by `pendingFetchResolversRef`
      //   above) -- this promise resolves on the query's next settlement instead, which is what
      //   `settleAfterRefresh` callers actually need to await.
      return new Promise<void>(resolve => {
        pendingFetchResolversRef.current.add(resolve);
      });
    },
    [eventId, getPayoutInfo],
  );

  const [setupCreditCard, setSetupCreditCard] = useManagedSearchParam('setupCreditCard');
  const [payoutRefresh, setPayoutRefresh] = useManagedSearchParam(PAYOUT_REFRESH_PARAM);
  const [postKycAction, setPostKycAction] = useManagedSearchParam('postKycAction');
  const [, setAddCashFund] = useManagedSearchParam('addCashFund');

  // Promotes the postKycAction URL flag set by a caller (e.g. the bank-account banner) into the
  // actual downstream URL state once KYC-later submit succeeds — keeps the chain URL-driven and
  // matches the same pattern setupCreditCard uses.
  const handleDialogSubmissionSuccess = useCallback(() => {
    if (postKycAction === 'addCashFund') {
      setPostKycAction(null);
      setAddCashFund('true');
    }
  }, [postKycAction, setPostKycAction, setAddCashFund]);

  const handleDialogClose = useCallback(() => {
    setSetupCreditCard(null);
    setPostKycAction(null);
  }, [setSetupCreditCard, setPostKycAction]);
  const [checkStripeStatus, setCheckStripeStatus] = useManagedSearchParam(CHECK_STRIPE_STATUS_PARAM);
  const [preloadedOrderIdForRefund] = useManagedSearchParam('preloadedOrderIdForRefund');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isBankConnected = !!payoutAccountInfo && canWithdrawFunds(payoutAccountInfo.status);

  const {
    handleTransfer,
    withdraw,
    loadingPayoutRequest,
    isOpenPayoutDialog,
    onOpenPayoutDialog,
    onClosePayoutDialog,
    payoutStatus,
    dialogStep,
    amountInMinorUnits,
    onAmountChange,
    onContinueToMethod,
    onContinueToAmount,
    onBackToAmount,
    selectedMethod,
    onMethodChange,
  } = usePayoutDialogController(fetchPayoutInfo, payoutAccountInfo, eventId as string);

  const refreshEditedPayoutAccount = useCallback(async () => {
    // Await this exact forced read, not the settlement of an unrelated background poll.
    const result = await payoutClient.query<GetPayoutAccountInfoQuery, GetPayoutAccountInfoQueryVariables>({
      query: GetPayoutAccountInfoDocument,
      variables: { eventId, options: { forceRefresh: true } },
      fetchPolicy: 'no-cache',
    });
    if (result.errors?.length || !result.data?.getRegistryEventData?.payoutAccount) throw new Error('Payout account refresh failed');
    applyPayoutInfo(result.data);
  }, [payoutClient, eventId, applyPayoutInfo]);
  const fetchPayoutSession = useRegistryPayoutAccountSession(eventId);
  const { transferSheetEnabled, shouldMountTransferSheet } = useTransferSheetRollout(isOpenPayoutDialog, canManagePayouts, payoutAccountInfo?.status === 'readyToUse');
  const handleConnectBank = useCallback(() => {
    onClosePayoutDialog();
    // Stripe Account Links expire and are single-use; mint a fresh one on every hand-off.
    initKYCOnboarding();
  }, [onClosePayoutDialog, initKYCOnboarding]);

  // const dialogRef = useOuterClick(onToggle, isOpen);
  // State will be handle by a query param, if this query param is present the modal will close and open.
  useEffect(() => {
    if (setupCreditCard) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, setupCreditCard, onClose]);

  usePostStripeReturnEffect({
    checkStripeStatus,
    fetchingPayoutInfo,
    payoutAccountInfo,
    preloadedOrderIdForRefund,
    setSetupCreditCard,
    setCheckStripeStatus,
  });

  // we don't clear out historical failures, so we only emphasize the last withdrawal
  const lastWithdrawal = payoutAccountInfo?.withdrawals.at(-1);
  const hasFailedWithdrawals = useMemo(() => lastWithdrawal?.status === RegistryPayoutAccountWithdrawalStatus.failed, [lastWithdrawal]);
  const failedWithdrawalReasonCode = useMemo(() => (hasFailedWithdrawals ? (lastWithdrawal?.failureReasonCode ?? null) : null), [hasFailedWithdrawals, lastWithdrawal]);
  const hasPendingWithdrawals = useMemo(
    () => payoutAccountInfo?.withdrawals.some(withdrawal => withdrawal.status === RegistryPayoutAccountWithdrawalStatus.pending) ?? false,
    [payoutAccountInfo],
  );

  const prevHasPendingWithdrawals = usePreviousValue(hasPendingWithdrawals);
  const [hasDoneWithdrawal, setHasDoneWithdrawal] = useState(false);
  useEffect(() => {
    // a withdrawal is pending, so clear done withdrawal
    if (hasPendingWithdrawals) {
      setHasDoneWithdrawal(false);
      return;
    }
    // previous withdrawal has been completed without error
    if (!hasPendingWithdrawals && prevHasPendingWithdrawals && !hasFailedWithdrawals) {
      setHasDoneWithdrawal(true);
      return;
    }
  }, [hasFailedWithdrawals, prevHasPendingWithdrawals, hasPendingWithdrawals, setHasDoneWithdrawal]);
  useEffect(() => {
    return withWindow(
      global => {
        let timeout: number;
        if (payoutAccountInfo && (payoutAccountInfo.status === 'pending' || hasPendingWithdrawals)) {
          if (attempts <= MAX_ATTEMPTS) {
            timeout = global.setTimeout(() => {
              setAttempts(prevValue => prevValue + 1);
              void fetchPayoutInfo();
            }, 10_000);
          }
        }

        return () => {
          if (timeout) {
            global.clearTimeout(timeout);
          }
        };
      },
      () => {},
    );
  }, [payoutAccountInfo, fetchPayoutInfo, eventId, attempts, setAttempts, hasPendingWithdrawals]);

  useEffect(() => {
    void fetchPayoutInfo();
  }, [fetchPayoutInfo, eventId]);
  // Returning from Stripe's hosted remediation flow. The forced read is load-bearing: the couple
  //   gets back well inside the backend's 5-minute status cache window, so an unforced refetch
  //   would hand back the same blocked status and the CTA would never clear.
  useEffect(() => {
    if (payoutRefresh !== 'true') return;
    setPayoutRefresh(null);
    void fetchPayoutInfo({ forceRefresh: true });
  }, [payoutRefresh, setPayoutRefresh, fetchPayoutInfo]);
  return (
    <Provider
      value={{
        eventId,
        hasDoneWithdrawal,
        hasFailedWithdrawals,
        failedWithdrawalReasonCode,
        hasPendingWithdrawals,
        canManagePayouts,
        setSetupCreditCard,
        fetchingPayoutInfo,
        payoutInfoChecked: hasFetchedPayoutInfo && !fetchingPayoutInfo,
        payoutAccountInfo,
        maxAttemptsReached,
        initKYCOnboarding,
        kycReady: !resolvedLocaleLoading,
        isLocaleSupported,
        isGetCashEquivalentSupported,
        resolvedCashFundCountryCode: resolvedCountryCode,
        resolvedCashFundCurrencyCode: resolvedCurrencyCode,
        hasKYCFailed,
        fetchPayoutInfo,
        onOpenPayoutDialog,
        initKYCOnboardingFromRefund,
        initKYCRemediation,
        isRemediationLinkLoading: fetchingOnboardingUrl,
        initKYCLaterOnboarding,
        initPayoutAccountLoading: initPayoutAccountOrLocaleLoading,
        initKYCLaterLoading,
      }}
    >
      {children}
      <CreditCardSetupInitiatorDialog
        payoutAccountInfo={payoutAccountInfo}
        canManagePayouts={canManagePayouts}
        onClose={handleDialogClose}
        onSubmissionSuccess={handleDialogSubmissionSuccess}
        isOpen={isOpen}
        initKYCOnboarding={initKYCOnboarding}
        initPayoutAccountLoading={initPayoutAccountOrLocaleLoading}
        initKYCLaterOnboarding={initKYCLaterOnboarding}
        initKYCLaterLoading={initKYCLaterLoading}
        maxAttemptsReached={maxAttemptsReached}
        isValidUSAddress={isValidUSAddress}
        isLocaleSupported={isLocaleSupported}
        shouldSkipAddressValidation={shouldSkipAddressValidation}
        handleInvalidShippingAddress={handleInvalidShippingAddress}
        handleChangeCurrency={handleChangeCurrency}
        registryCurrencyCode={registryCurrencyCode}
        shippingCountryCode={registryShippingAddress?.countryCode ?? undefined}
        forceFullKyc={setupCreditCard === 'fullKyc'}
      />
      {payoutAccountInfo &&
        (transferSheetEnabled ? (
          shouldMountTransferSheet && (
            <ChunkErrorBoundary
              section="transfer-sheet"
              fallback={
                <PayoutDialog
                  isOpen={isOpenPayoutDialog}
                  onClose={onClosePayoutDialog}
                  payoutAccountInfo={payoutAccountInfo}
                  dialogStep={dialogStep}
                  amountInMinorUnits={amountInMinorUnits}
                  onAmountChange={onAmountChange}
                  onContinue={onContinueToMethod}
                  onBack={onBackToAmount}
                  selectedMethod={selectedMethod}
                  onMethodChange={onMethodChange}
                  onTransfer={handleTransfer}
                  loadingTransfer={loadingPayoutRequest}
                  status={payoutStatus}
                  onConnectBank={isBankConnected ? onContinueToAmount : handleConnectBank}
                  onBankPromoViewed={partner => registryTelemetry.transferDialogBankPromoViewed(partner)}
                  onBankPromoInteracted={(cta, partner) => registryTelemetry.transferDialogBankPromoInteracted(cta, partner)}
                />
              }
            >
              <LazyPartnerTransferSheet
                isOpen={isOpenPayoutDialog}
                eventId={eventId}
                onClose={onClosePayoutDialog}
                payoutAccountInfo={payoutAccountInfo}
                withdraw={(amount, method) => withdraw(amount, method as RegistryPayoutAccountExternalAccountPayoutMethod)}
                onConnectBank={handleConnectBank}
                bankAccountEditor={{ fetchSession: fetchPayoutSession, refreshAccount: refreshEditedPayoutAccount }}
                onStatusDone={status => telemetry.closePayoutDialogStatusButtonClicked(status)}
                onPartnerPromoViewed={(partner, placement, label) => registryTelemetry.transferDialogBankPromoViewed(partner, placement, label)}
                onPartnerPromoInteracted={(cta, partner, placement, details) => registryTelemetry.transferDialogBankPromoInteracted(cta, partner, placement, details)}
              />
            </ChunkErrorBoundary>
          )
        ) : (
          <PayoutDialog
            isOpen={isOpenPayoutDialog}
            onClose={onClosePayoutDialog}
            payoutAccountInfo={payoutAccountInfo}
            dialogStep={dialogStep}
            amountInMinorUnits={amountInMinorUnits}
            onAmountChange={onAmountChange}
            onContinue={onContinueToMethod}
            onBack={onBackToAmount}
            selectedMethod={selectedMethod}
            onMethodChange={onMethodChange}
            onTransfer={handleTransfer}
            loadingTransfer={loadingPayoutRequest}
            status={payoutStatus}
            onConnectBank={isBankConnected ? onContinueToAmount : handleConnectBank}
            onBankPromoViewed={partner => registryTelemetry.transferDialogBankPromoViewed(partner)}
            onBankPromoInteracted={(cta, partner) => registryTelemetry.transferDialogBankPromoInteracted(cta, partner)}
          />
        ))}
    </Provider>
  );
};

export { CreditCardSetupProvider };
